import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import ImageUploader from "@/components/ImageUploader";
import RecurrencePicker from "@/components/RecurrencePicker";
import { toast } from "sonner";
import { CalendarIcon, X, Save, Trash2, Heart, ShieldCheck } from "lucide-react";

const CATEGORIES = ["food", "art", "craft", "vintage", "plants", "music", "wellness", "other"];
const DRAFT_KEY = "addvendtour:new-event-draft";
const EMPTY_FORM = {
  title: "", description: "", category: "craft",
  // NEW: multi-select. `category` (singular) stays populated as
  // `categories[0]` for backward compat with existing list filters.
  categories: ["craft"],
  location: "", city: "",
  booth_fee: 0, max_vendors: 20, cover_image: "", tags: "", venue_url: "", social_url: "",
  event_time: "",
  is_fundraiser: false,
  beneficiary_user_id: "",
  fundraising_goal: "",
};

export default function CreateEvent() {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const [form, setForm] = useState(EMPTY_FORM);
  const [nonprofits, setNonprofits] = useState([]);
  const [nonprofitsLoaded, setNonprofitsLoaded] = useState(false);
  const [date, setDate] = useState(null);
  const [schedule, setSchedule] = useState({
    recurrence: null,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York",
  });
  const [busy, setBusy] = useState(false);
  const [draftRestored, setDraftRestored] = useState(false);
  const hydratedRef = useRef(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // Hydrate draft on mount (once)
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved && typeof saved === "object") {
          const hasContent = Object.entries(saved.form || {}).some(
            ([k, v]) => v && !["category", "booth_fee", "max_vendors"].includes(k)
          );
          if (hasContent || saved.date) {
            if (saved.form) setForm({ ...EMPTY_FORM, ...saved.form });
            if (saved.date) setDate(new Date(saved.date));
            setDraftRestored(true);
          }
        }
      }
    } catch {
      // Ignore corrupt drafts; treat as empty.
    }
    hydratedRef.current = true;
  }, []);

  // Auto-save every time form or date changes (only after initial hydrate).
  useEffect(() => {
    if (!hydratedRef.current) return;
    try {
      const payload = {
        form,
        date: date ? date.toISOString() : null,
        savedAt: Date.now(),
      };
      localStorage.setItem(DRAFT_KEY, JSON.stringify(payload));
    } catch {
      // localStorage disabled (private mode, quota, etc.) — silently ignore.
    }
  }, [form, date]);

  const clearDraft = () => {
    try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
    setForm(EMPTY_FORM);
    setDate(null);
    setDraftRestored(false);
    toast.success("Draft cleared");
  };

  // Fetch verified nonprofits lazily when the fundraiser toggle turns on
  useEffect(() => {
    if (form.is_fundraiser && !nonprofitsLoaded) {
      (async () => {
        try {
          const { data } = await api.get("/nonprofits/verified", { params: { limit: 200 } });
          const list = (data.items || []).filter((n) => n.accepts_donations);
          setNonprofits(list);
        } catch { /* ignore */ }
        finally { setNonprofitsLoaded(true); }
      })();
    }
  }, [form.is_fundraiser, nonprofitsLoaded]);

  const submit = async (e) => {
    e.preventDefault();
    if (!date) { toast.error("Please pick a date"); return; }
    // Weekly + monthly need a stop date and a picked day/weekday combo. Fail
    // fast with a friendly toast rather than letting the server 400.
    const rec = schedule.recurrence;
    if (rec?.pattern === "weekly" && (!rec.weekdays?.length || !rec.until)) {
      toast.error("Weekly events need at least one day and a stop date");
      return;
    }
    if (rec?.pattern === "monthly" && !rec.until) {
      toast.error("Monthly events need a stop date");
      return;
    }
    if (rec?.pattern === "custom" && !(rec.custom_dates?.length)) {
      toast.error("Add at least one custom date");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        booth_fee: parseFloat(form.booth_fee) || 0,
        max_vendors: parseInt(form.max_vendors) || 20,
        date: date.toISOString().slice(0, 10),
        tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean),
        // Multi-category: send BOTH so the server picks the modern one.
        categories: form.categories && form.categories.length ? form.categories : [form.category],
        category: (form.categories && form.categories[0]) || form.category,
        timezone: schedule.timezone || null,
        event_time: form.event_time || null,
        recurrence: schedule.recurrence || null,
        is_fundraiser: !!form.is_fundraiser,
        beneficiary_user_id: form.is_fundraiser ? (form.beneficiary_user_id || null) : null,
        fundraising_goal_cents: form.is_fundraiser && form.fundraising_goal
          ? Math.round(parseFloat(form.fundraising_goal) * 100) : null,
      };
      const { data } = await api.post("/events", payload);
      try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
      toast.success(data.is_recurring
        ? `Event series created · ${data.occurrences?.length || 0} dates`
        : "Event created");
      navigate(`/events/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to create");
    } finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-3xl px-6 py-14">
      <div className="overline" style={{ color: "var(--terracotta)" }}>New event</div>
      <h1 className="font-serif text-5xl mt-2">Post your event.</h1>
      <p className="mt-3 text-sm" style={{ color: "var(--ink-soft)" }}>Vendors will discover and apply. You approve who you want.</p>

      {draftRestored && (
        <div
          data-testid="draft-restored-banner"
          className="mt-6 rounded-md p-3 flex items-start gap-3 border"
          style={{
            backgroundColor: "rgba(111,125,85,0.08)",
            borderColor: "rgba(111,125,85,0.25)",
          }}
        >
          <Save size={16} className="mt-0.5 shrink-0" style={{ color: "var(--moss)" }} />
          <div className="flex-1 text-sm" style={{ color: "var(--ink)" }}>
            <strong>Draft restored.</strong>{" "}
            <span style={{ color: "var(--ink-soft)" }}>
              We saved what you were typing so you didn&apos;t lose it while switching tabs.
            </span>
          </div>
          <button
            type="button"
            onClick={clearDraft}
            data-testid="draft-clear"
            className="text-xs inline-flex items-center gap-1 underline whitespace-nowrap"
            style={{ color: "var(--ink-soft)" }}
          >
            <Trash2 size={12} />
            Start fresh
          </button>
        </div>
      )}

      <form onSubmit={submit} className="mt-10 space-y-6">
        <Field label="Title"><Input data-testid="new-title" value={form.title} onChange={upd("title")} required /></Field>
        <Field label="Description"><Textarea data-testid="new-description" rows={4} value={form.description} onChange={upd("description")} required /></Field>
        <div className="grid sm:grid-cols-2 gap-6">
          <Field label="Categories" hint="Pick any that apply — we'll match vendors in your area whose profile lists at least one of these.">
            <div className="flex flex-wrap gap-1.5" data-testid="new-categories">
              {CATEGORIES.map((c) => {
                const selected = (form.categories || []).includes(c);
                return (
                  <button
                    type="button"
                    key={c}
                    data-testid={`new-category-chip-${c}`}
                    onClick={() => {
                      const cur = new Set(form.categories || []);
                      if (cur.has(c)) cur.delete(c); else cur.add(c);
                      const next = Array.from(cur);
                      setForm({ ...form, categories: next, category: next[0] || "" });
                    }}
                    className="px-3 py-1.5 rounded-full text-xs font-semibold transition-colors border"
                    style={{
                      backgroundColor: selected ? "var(--avt-purple)" : "transparent",
                      color: selected ? "#fff" : "var(--ink)",
                      borderColor: selected ? "var(--avt-purple)" : "var(--border-warm)",
                    }}
                  >
                    {c}
                  </button>
                );
              })}
            </div>
          </Field>
          <Field label="Date">
            <Popover>
              <PopoverTrigger asChild>
                <Button type="button" variant="outline" data-testid="new-date" className="w-full justify-start">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {date ? date.toDateString() : "Pick a date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="p-0">
                <Calendar mode="single" selected={date} onSelect={setDate} />
              </PopoverContent>
            </Popover>
          </Field>
        </div>
        <div className="grid sm:grid-cols-2 gap-6">
          <Field label="Start time (local, optional)">
            <Input type="time" data-testid="new-event-time"
              value={form.event_time} onChange={upd("event_time")} placeholder="10:00" />
          </Field>
        </div>
        <RecurrencePicker
          value={schedule}
          onChange={setSchedule}
          startDate={date ? date.toISOString().slice(0, 10) : null}
        />
        <div className="grid sm:grid-cols-2 gap-6">
          <Field label="Venue / location"><Input data-testid="new-location" value={form.location} onChange={upd("location")} required /></Field>
          <Field label="City"><Input data-testid="new-city" value={form.city} onChange={upd("city")} required /></Field>
        </div>
        <div className="grid sm:grid-cols-2 gap-6">
          <Field label="Booth fee (USD, 0 for free)"><Input type="number" step="0.01" min="0" data-testid="new-fee" value={form.booth_fee} onChange={upd("booth_fee")} /></Field>
          <Field label="Max vendors"><Input type="number" min="1" data-testid="new-max" value={form.max_vendors} onChange={upd("max_vendors")} /></Field>
        </div>
        <Field label="Cover image">
          {form.cover_image ? (
            <div className="relative inline-block">
              <img src={form.cover_image} alt="cover" className="max-h-48 rounded-sm" data-testid="new-cover-preview" />
              <button type="button" onClick={() => setForm({ ...form, cover_image: "" })} className="absolute top-1 right-1 bg-white/90 rounded-full p-1" data-testid="new-cover-remove">
                <X className="h-3 w-3" />
              </button>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2 items-center">
              <ImageUploader label="Upload cover image" testId="new-cover" onUploaded={(url) => setForm({ ...form, cover_image: url })} />
              <span className="text-xs" style={{ color: "var(--ink-soft)" }}>or paste URL:</span>
              <Input data-testid="new-cover-url" value={form.cover_image} onChange={upd("cover_image")} placeholder="https://…" className="flex-1 min-w-[200px]" />
            </div>
          )}
        </Field>
        <Field label="Tags (comma-separated)"><Input data-testid="new-tags" value={form.tags} onChange={upd("tags")} placeholder="handmade, organic, family-friendly" /></Field>
        <Field label="Official venue / event page URL (helps vendors trust your listing)">
          <Input
            data-testid="new-venue-url"
            type="url"
            value={form.venue_url}
            onChange={upd("venue_url")}
            placeholder="https://venuewebsite.com/event or https://maps.google.com/…"
          />
        </Field>
        <Field label="Social media link (Instagram, Facebook, TikTok…)">
          <Input
            data-testid="new-social-url"
            type="url"
            value={form.social_url}
            onChange={upd("social_url")}
            placeholder="https://instagram.com/your-event"
          />
        </Field>

        {/* ---- Fundraiser (Give Back Marketplace) ---- */}
        <div
          data-testid="fundraiser-section"
          className="rounded-lg p-5 border-2 space-y-4"
          style={{
            borderColor: form.is_fundraiser ? "var(--avt-pink)" : "var(--border-warm)",
            backgroundColor: form.is_fundraiser ? "rgba(236,72,153,0.05)" : "transparent",
          }}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <Heart className="h-4 w-4" style={{ color: "var(--avt-pink)" }} fill="var(--avt-pink)" strokeWidth={0} />
                <span className="text-xs uppercase tracking-widest font-semibold" style={{ color: "var(--avt-pink)" }}>
                  Give Back Marketplace
                </span>
              </div>
              <div className="mt-1 text-base font-semibold">Turn this into a fundraiser</div>
              <p className="mt-1 text-xs" style={{ color: "var(--ink-soft)" }}>
                Donors can give directly to a verified 501(c)(3) at this event.
                AddVendTour takes 0% &mdash; 100% goes to the nonprofit.
              </p>
            </div>
            <Switch
              data-testid="fundraiser-toggle"
              checked={!!form.is_fundraiser}
              onCheckedChange={(v) => setForm({ ...form, is_fundraiser: v })}
            />
          </div>

          {form.is_fundraiser && (
            <div className="space-y-3 pt-2" data-testid="fundraiser-fields">
              <div>
                <Label className="text-xs uppercase tracking-widest">Beneficiary (verified 501(c)(3))</Label>
                <div className="mt-2">
                  {nonprofits.length === 0 && nonprofitsLoaded && authUser?.nonprofit_status !== "verified" ? (
                    <div className="p-3 rounded border text-xs" style={{ borderColor: "var(--border-warm)", color: "var(--ink-soft)" }}>
                      No verified nonprofits with donations enabled yet.{" "}
                      <Link to="/verify-nonprofit" className="underline" style={{ color: "var(--avt-purple)" }}>
                        Verify your own 501(c)(3)
                      </Link>{" "}
                      or come back once one has connected Stripe.
                    </div>
                  ) : (
                    <Select
                      value={form.beneficiary_user_id || ""}
                      onValueChange={(v) => setForm({ ...form, beneficiary_user_id: v })}
                    >
                      <SelectTrigger data-testid="fundraiser-beneficiary-select">
                        <SelectValue placeholder="Pick a nonprofit…" />
                      </SelectTrigger>
                      <SelectContent>
                        {authUser?.nonprofit_status === "verified" && (
                          <SelectItem value={authUser.id}>
                            My own org &mdash; {authUser.nonprofit_org_name || authUser.name}
                          </SelectItem>
                        )}
                        {nonprofits.map((n) => (
                          <SelectItem key={n.id} value={n.id}>
                            {n.nonprofit_org_name || n.business_name || n.name}
                            {n.city ? ` · ${n.city}` : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <p className="mt-2 text-[11px]" style={{ color: "var(--ink-soft)" }}>
                  Only verified 501(c)(3) accounts with Stripe Connect enabled appear here.
                </p>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-widest">Fundraising goal (optional, USD)</Label>
                <div className="mt-2 relative max-w-xs">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm" style={{ color: "var(--ink-soft)" }}>$</span>
                  <Input
                    data-testid="fundraiser-goal"
                    type="number"
                    min="0"
                    step="50"
                    className="pl-7"
                    value={form.fundraising_goal}
                    onChange={upd("fundraising_goal")}
                    placeholder="5000"
                  />
                </div>
                <p className="mt-1 text-[11px]" style={{ color: "var(--ink-soft)" }}>
                  Shown as a progress bar on the event page. Leave blank to just accept donations without a target.
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="rounded-sm p-4 text-xs border" style={{ backgroundColor: "rgba(111,125,85,0.06)", borderColor: "rgba(111,125,85,0.2)", color: "var(--ink-soft)" }} data-testid="create-event-trust-note">
          <strong>Heads up:</strong> new organizers are marked <em>unverified</em> and their first
          events go into a review queue before going live. Add a real venue URL, business website,
          and past photos to speed up verification. Verified organizers post instantly.
        </div>

        <Button type="submit" data-testid="new-submit" disabled={busy} className="btn-primary w-full py-6 rounded-full">
          {busy ? "Creating…" : "Publish event"}
        </Button>
      </form>
    </div>
  );
}

function Field({ label, children, hint }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest">{label}</Label>
      <div className="mt-2">{children}</div>
      {hint && (
        <div className="text-[11px] mt-1.5" style={{ color: "var(--ink-soft)" }}>
          {hint}
        </div>
      )}
    </div>
  );
}
